/**
 * Pending Image Storage Helpers
 *
 * Persists image file + prompt to sessionStorage so data survives the auth flow.
 * Used by HomePage and ImageToVideoPage when an unauthenticated user clicks Generate.
 */

const PENDING_FILE_KEY = 'pending_image_to_video_file';
const PENDING_PROMPT_KEY = 'pending_image_to_video_prompt';

/**
 * Save pending image and prompt to sessionStorage.
 * Called when user is not authenticated — persists data across auth flow.
 */
export function savePendingImageData(file: File, promptText: string): void {
  try {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const imageData = {
        name: file.name,
        type: file.type,
        size: file.size,
        base64,
      };
      sessionStorage.setItem(PENDING_FILE_KEY, JSON.stringify(imageData));
    };
    reader.readAsDataURL(file);

    if (promptText.trim()) {
      sessionStorage.setItem(PENDING_PROMPT_KEY, promptText.trim());
    }
  } catch (error) {
    console.error('❌ [pendingImageStorage] Failed to save pending image:', error);
  }
}

/**
 * Restore pending image and prompt from sessionStorage.
 * Called after user authenticates.
 */
export function restorePendingImageData(): { file: File; prompt: string; preview: string } | null {
  try {
    const savedImage = sessionStorage.getItem(PENDING_FILE_KEY);
    const savedPrompt = sessionStorage.getItem(PENDING_PROMPT_KEY);

    if (!savedImage) {
      return null;
    }

    const imageData = JSON.parse(savedImage);

    // Convert base64 back to File object
    const base64Data = imageData.base64.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: imageData.type });
    const file = new File([blob], imageData.name, { type: imageData.type });

    return {
      file,
      prompt: savedPrompt || '',
      preview: imageData.base64,
    };
  } catch (error) {
    console.error('❌ [pendingImageStorage] Failed to restore pending image:', error);
    return null;
  }
}

/**
 * Clear pending image data from sessionStorage.
 * Called after successful generation or when user manually resets.
 */
export function clearPendingImageData(): void {
  sessionStorage.removeItem(PENDING_FILE_KEY);
  sessionStorage.removeItem(PENDING_PROMPT_KEY);
}
